export type HonorificPrefix = "" | "Dr." | "Dra.";

const HONORIFIC_REGEX = /^(dr\.?|dra\.?)\s+/i;

// Portuguese/Brazilian surname particles — never used as first name or initials
const SURNAME_PARTICLES = new Set([
  "de", "da", "di", "do", "dos", "das", "du", "d",
  "e", "von", "van", "del", "della", "delle", "dei",
  "der", "den", "des", "af", "av",
]);

function isParticle(word: string): boolean {
  return SURNAME_PARTICLES.has(word.toLowerCase().replace(/[.']/g, ""));
}

/** Returns name words, skipping leading honorifics. */
function nameWords(name: string | null | undefined): string[] {
  return stripHonorificPrefix(name)
    .split(/\s+/)
    .filter(Boolean);
}

export function stripHonorificPrefix(name: string | null | undefined): string {
  if (!name) return "";
  return name.trim().replace(HONORIFIC_REGEX, "").trim();
}

export function normalizeHonorificPrefix(
  value: string | null | undefined,
): HonorificPrefix {
  if (!value) return "";
  const normalized = value.trim().toLowerCase();
  if (normalized === "dr" || normalized === "dr.") return "Dr.";
  if (normalized === "dra" || normalized === "dra.") return "Dra.";
  return "";
}

export function formatDisplayName(
  displayName: string | null | undefined,
  honorificPrefix: string | null | undefined,
): string {
  const cleanName = stripHonorificPrefix(displayName);
  const prefix = normalizeHonorificPrefix(honorificPrefix);
  if (!cleanName) return prefix;
  return prefix ? `${prefix} ${cleanName}` : cleanName;
}

/**
 * Returns initials skipping particles.
 * "Rafael de Oliveira" → "RO"
 * "Dr. Maria das Dores" → "MD"
 * "João" → "J"
 */
export function getNameInitials(
  displayName: string | null | undefined,
  honorificPrefix?: string | null,
): string {
  const words = nameWords(displayName);
  const significant = words.filter((w) => !isParticle(w));
  if (significant.length === 0) return "";
  const prefix = normalizeHonorificPrefix(honorificPrefix);
  // Take first + last significant word for initials (skips middle names/particles)
  const picks =
    significant.length === 1
      ? [significant[0]]
      : [significant[0], significant[significant.length - 1]];
  const initials = picks
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
  return prefix ? initials : initials;
}

/**
 * First non-particle word of the name, used in greetings.
 * "Rafael de Oliveira" → "Rafael"
 * "de Oliveira" → "Oliveira" (particle as first word → skip)
 */
export function formatGreetingName(
  displayName: string | null | undefined,
  honorificPrefix: string | null | undefined,
): string {
  const words = nameWords(displayName);
  const prefix = normalizeHonorificPrefix(honorificPrefix);
  const firstName = words.find((w) => !isParticle(w)) ?? words[0] ?? "";
  if (!firstName) return prefix || "Visitante";
  return prefix ? `${prefix} ${firstName}` : firstName;
}
